import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Image, Plus, Grid3X3, Star, FolderPlus, Trash2, Eye, EyeOff, 
  User, ImageIcon, Clock, Users, X, Check, Upload, Loader2, MoreVertical,
  ChevronRight, Settings } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Helper to get absolute image URL
const getImageUrl = (photo) => {
  const url = photo?.url || photo?.image_url || photo?.thumb_url;
  if (!url) return null;
  // If URL is already absolute, return as-is
  if (url.startsWith('http')) return url;
  // Prepend backend URL for relative paths
  return `${BACKEND_URL}${url}`;
};

/**
 * PHOTOS MANAGER — Full Photo System UI
 * 
 * Per LOCKED DIRECTIVE:
 * - Grid-first, simple, fast
 * - 3 columns mobile, 4 desktop
 * - Top tabs: Featured (6) | Library | Albums
 * - Edit panel: Visibility, Assignments, Album, Delete
 */
export default function PhotosManager() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [activeTab, setActiveTab] = useState('featured');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Photo data
  const [featuredSlots, setFeaturedSlots] = useState([null, null, null, null, null, null]);
  const [library, setLibrary] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Edit modal state
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Album creation
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  
  const fileInputRef = useRef(null);
  const isPersonalMember = ['personal_member', 'founder'].includes(user?.membership_tier) || user?.is_founder;

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    loadAllData();
  }, [user, navigate]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [libraryRes, featuredRes, albumsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/api/photos/library`, { headers }).catch(() => ({ data: { photos: [] } })),
        axios.get(`${API}/api/photos/featured`, { headers }).catch(() => ({ data: { featured: [] } })),
        axios.get(`${API}/api/photos/albums`, { headers }).catch(() => ({ data: { albums: [] } })),
        axios.get(`${API}/api/groups/my-groups`, { headers }).catch(() => ({ data: { groups: [] } }))
      ]);
      
      setLibrary(libraryRes.data?.photos || []);
      setGroups(groupsRes.data?.groups || []);
      setAlbums(albumsRes.data?.albums || []);
      
      // Map featured photos to slots
      const featured = featuredRes.data?.featured || [];
      const slots = [null, null, null, null, null, null];
      featured.forEach((photo, idx) => {
        if (idx < 6) slots[idx] = photo;
      });
      setFeaturedSlots(slots);
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be less than 8MB');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      setUploadProgress(10);
      const slotRes = await axios.post(`${API}/api/media/request-upload`, { count: 1, mime_types: [file.type] }, { headers });
      const slot = slotRes.data?.slots?.[0];
      if (!slot) throw new Error('Failed to get upload slot');
      
      setUploadProgress(30);
      await axios.post(`${API}${slot.upload_url.replace('/api', '')}`, file, {
        headers: { ...headers, 'Content-Type': file.type },
        onUploadProgress: (p) => setUploadProgress(30 + Math.round((p.loaded / p.total) * 50))
      });
      
      setUploadProgress(90);
      await axios.post(`${API}/api/media/confirm`, { photo_id: slot.photo_id }, { headers });
      setUploadProgress(100);
      
      toast.success('Photo uploaded!');
      loadAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail?.message || error.response?.data?.detail || 'Upload failed';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetFeatured = async (photo, slot) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/featured/add`, { 
        photo_id: photo.photo_id, 
        slot: slot 
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success(`Added to Featured slot ${slot + 1}`);
      loadAllData();
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to add to featured');
    }
  };

  const handleRemoveFromFeatured = async (photo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/featured/remove`, { 
        photo_id: photo.photo_id 
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Removed from Featured');
      loadAllData();
    } catch (error) {
      toast.error('Failed to remove from featured');
    }
  };

  const handleSetAsProfile = async (photo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/${photo.photo_id}/set-profile`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Profile photo updated');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to set profile photo');
    }
  };

  const handleSetAsHeader = async (photo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/${photo.photo_id}/set-header-background`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Header background updated');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to set header background');
    }
  };

  const handleUpdateVisibility = async (photo, visibility) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/photos/${photo.photo_id}/visibility`, { 
        visibility 
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Visibility updated');
      loadAllData();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleSendToTimeline = async (photo) => {
    if (!isPersonalMember) {
      toast.error('Personal Members only');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/${photo.photo_id}/send-to-timeline`, {
        caption: ''
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Added to SCUTA timeline');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to send to timeline');
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/photos/${photo.photo_id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Photo deleted');
      setShowEditModal(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleCreateAlbum = async () => {
    if (!newAlbumName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/photos/albums/create`, { 
        name: newAlbumName.trim() 
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Album created');
      setNewAlbumName('');
      setShowAlbumModal(false);
      loadAllData();
    } catch (error) {
      toast.error('Failed to create album');
    }
  };

  const openEditModal = (photo) => {
    setSelectedPhoto(photo);
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/settings/my-world')} className="p-2 -ml-2 rounded-lg hover:opacity-70">
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
              </button>
              <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Photos</h1>
            </div>
            <Button 
              onClick={() => fileInputRef.current?.click()} 
              disabled={uploading}
              style={{ backgroundColor: 'var(--lk-sage)' }}
              data-testid="upload-photo-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add
            </Button>
            <input 
              ref={fileInputRef} 
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={handleFileUpload} 
            />
          </div>
        </div>
      </header>

      {/* Upload Progress */}
      {uploading && (
        <div className="px-4 py-2" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--lk-sage)' }} />
              <div className="flex-1">
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--fy-border-subtle)' }}>
                  <div className="h-full transition-all" style={{ width: `${uploadProgress}%`, backgroundColor: 'var(--lk-sage)' }} />
                </div>
              </div>
              <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{uploadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="sticky top-14 z-30" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'featured', label: 'Featured (6)', icon: Star },
              { id: 'library', label: 'Library', icon: Grid3X3 },
              { id: 'albums', label: 'Albums', icon: FolderPlus }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'border-b-2' : ''
                }`}
                style={{ 
                  borderColor: activeTab === tab.id ? 'var(--lk-sage)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--lk-sage)' : 'var(--fy-text-secondary)'
                }}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* FEATURED TAB */}
        {activeTab === 'featured' && (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              These 6 photos appear on your Base Camp page. Tap a slot to assign a photo.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {featuredSlots.map((photo, idx) => (
                <div 
                  key={idx} 
                  className="aspect-square rounded-xl overflow-hidden relative"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '2px dashed var(--fy-border-subtle)' }}
                >
                  {photo ? (
                    <>
                      <img 
                        src={getImageUrl(photo)} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => handleRemoveFromFeatured(photo)}
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/50"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                      <span className="absolute bottom-1 left-1 text-xs px-1.5 py-0.5 rounded bg-black/50 text-white">
                        {idx + 1}
                      </span>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setActiveTab('library');
                        toast.info('Select a photo to feature');
                      }}
                      className="w-full h-full flex flex-col items-center justify-center gap-1"
                      data-testid={`featured-slot-${idx}`}
                    >
                      <Plus className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                      <span className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>Add media</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LIBRARY TAB */}
        {activeTab === 'library' && (
          <div className="space-y-4">
            {library.length === 0 ? (
              <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <Image className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>No photos yet</p>
                <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>Upload photos to your library</p>
                <Button onClick={() => fileInputRef.current?.click()} style={{ backgroundColor: 'var(--lk-sage)' }}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Photo
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {library.map((photo) => (
                  <button
                    key={photo.photo_id}
                    onClick={() => openEditModal(photo)}
                    className="aspect-square rounded-lg overflow-hidden relative group"
                    data-testid={`photo-${photo.photo_id}`}
                  >
                    <img 
                      src={getImageUrl(photo)} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <MoreVertical className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {/* Visibility indicator */}
                    <span className="absolute bottom-1 right-1">
                      {photo.visibility === 'private' ? (
                        <EyeOff className="w-3 h-3 text-white drop-shadow" />
                      ) : (
                        <Eye className="w-3 h-3 text-white drop-shadow" />
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ALBUMS TAB */}
        {activeTab === 'albums' && (
          <div className="space-y-4">
            <Button 
              onClick={() => setShowAlbumModal(true)} 
              variant="outline" 
              className="w-full"
              data-testid="create-album-btn"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> Create Album
            </Button>
            
            {albums.length === 0 ? (
              <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <FolderPlus className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
                <p className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>No albums yet</p>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>Create albums to organize your photos</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {albums.map((album) => (
                  <Card 
                    key={album.album_id} 
                    className="p-4 flex items-center gap-3"
                    style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                  >
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                      <FolderPlus className="w-6 h-6" style={{ color: 'var(--lk-sage)' }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>{album.name}</p>
                      <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{album.photo_count || 0} photos</p>
                    </div>
                    <ChevronRight className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* EDIT PHOTO MODAL */}
      {showEditModal && selectedPhoto && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <Card 
            className="w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--fy-background-primary)', maxHeight: '90vh' }}
          >
            <div className="overflow-y-auto" style={{ maxHeight: '90vh' }}>
              {/* Preview */}
              <div className="relative aspect-video">
                <img 
                  src={getImageUrl(selectedPhoto)} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setShowEditModal(false)}
                  className="absolute top-3 right-3 p-2 rounded-full bg-black/50"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Actions */}
              <div className="p-4 space-y-4">
                {/* Visibility */}
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Visibility</label>
                  <div className="flex gap-2 mt-2">
                    {['private', 'contacts', 'groups'].map(v => (
                      <button
                        key={v}
                        onClick={() => handleUpdateVisibility(selectedPhoto, v)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors`}
                        style={{ 
                          backgroundColor: selectedPhoto.visibility === v ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                          color: selectedPhoto.visibility === v ? 'white' : 'var(--fy-text-secondary)'
                        }}
                      >
                        {v.charAt(0).toUpperCase() + v.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Featured Slot Assignment */}
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Add to Featured</label>
                  <div className="flex gap-2 mt-2">
                    {[0, 1, 2, 3, 4, 5].map(slot => (
                      <button
                        key={slot}
                        onClick={() => handleSetFeatured(selectedPhoto, slot)}
                        className="w-10 h-10 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        style={{ 
                          backgroundColor: featuredSlots[slot]?.photo_id === selectedPhoto.photo_id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                          color: featuredSlots[slot]?.photo_id === selectedPhoto.photo_id ? 'white' : 'var(--fy-text-secondary)'
                        }}
                      >
                        {slot + 1}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assignment Actions */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleSetAsProfile(selectedPhoto)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                  >
                    <User className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                    <span style={{ color: 'var(--fy-text-primary)' }}>Set as Profile Photo</span>
                  </button>
                  
                  <button
                    onClick={() => handleSetAsHeader(selectedPhoto)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                  >
                    <ImageIcon className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                    <span style={{ color: 'var(--fy-text-primary)' }}>Set as Header Background</span>
                  </button>
                  
                  {isPersonalMember && (
                    <button
                      onClick={() => handleSendToTimeline(selectedPhoto)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg"
                      style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                    >
                      <Clock className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
                      <span style={{ color: 'var(--fy-text-primary)' }}>Send to SCUTA Timeline</span>
                    </button>
                  )}
                </div>

                {/* Delete */}
                <button
                  onClick={() => handleDeletePhoto(selectedPhoto)}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626' }}
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Delete Photo</span>
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* CREATE ALBUM MODAL */}
      {showAlbumModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Create Album</h3>
              <button onClick={() => setShowAlbumModal(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={newAlbumName}
              onChange={(e) => setNewAlbumName(e.target.value)}
              placeholder="Album name"
              className="w-full p-3 rounded-lg mb-4"
              style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
              data-testid="album-name-input"
            />
            <Button 
              onClick={handleCreateAlbum} 
              className="w-full" 
              disabled={!newAlbumName.trim()}
              style={{ backgroundColor: 'var(--lk-sage)' }}
            >
              Create Album
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
